import React, { useEffect, forwardRef, createRef, useState } from 'react';
import { Alert, Grid, Typography } from '@mui/material';
import { RootineCard, CardProps, RootineTextField } from '../atoms';
import useAPI from '../utils/useApi';
import { APIRequests } from '../config/api';
import {
  UserBloodUploadOut,
  UserBloodUploadIn,
  BloodTestQuestion,
  BloodTestQuestionIn,
  RequestInput,
} from '../types';
import userServices from '../utils/user';
import { Loading } from './Layout/Loading';
import { useForm } from 'react-hook-form';
import RootineButton from '../atoms/RootineButton';
import { useNavigate } from 'react-router-dom';

const disclaimerText = `
DISCLAIMER: Rootine uses the provided blood level data to calculate final nutrient recommendations based on reference ranges from specific sources, which could vary by lab and by country. We are not responsible for ensuring these entered values are correct and/or match the values shown on the results from the individual's test. Rootine should not be used to treat, diagnose, or cure any health issues including severe deficiencies. Consult with your health care professional regarding the nutrient dosages in Rootine.
`;
const disclaimerNote = `
PLEASE NOTE: Not all testing labs analyze for certain nutrient levels, which may be missing from your data set. Rootine generates your nutrient recommendations based on the data available.
`;
export const BloodUploadForm = (): JSX.Element => {
  const [submitError, setSubmitError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [questions, setQuestions] = useState<BloodTestQuestion[]>();
  const [formData, setFormData] = useState<{
    [name: string]: string | undefined;
  }>();
  const [isUploaded, setIsUploaded] = useState<boolean>(false);
  let bloodData = useAPI<UserBloodUploadOut>(APIRequests.UserBloodTestDataList);
  let bloodDataUpdate = useAPI<UserBloodUploadIn>(
    APIRequests.UserBloodTestDataUpdate
  );
  const userToken = userServices.getToken();
  const userId = userServices.getId();
  const { handleSubmit, control } = useForm();
  const navigate = useNavigate();

  useEffect(() => {
    bloodData.execute({});
  }, []);

  useEffect(() => {
    if (bloodData.data) {
      setQuestions(bloodData.data.answers);
      let idResultMap: { [name: string]: string | undefined } = {};
      bloodData.data.answers.forEach((item) => {
        idResultMap[item.question_id] = item.result;
      });
      setFormData(idResultMap);
    }
  }, [bloodData.data]);

  useEffect(() => {
    if (bloodDataUpdate.data) {
      setSuccess(true);
    }
  }, [bloodDataUpdate.data]);

  const onSubmit = (data: any) => {
    setSuccess(false);
    const newdata: BloodTestQuestionIn[] = [];
    Object.keys(data).forEach((k: string) => {
      const val = data[k];
      if (val) {
        let q = questions?.filter((question) => question.code === k)[0];
        let record: BloodTestQuestionIn = {
          question_id: q ? q.question_id : '',
          value: data[k],
          id: q ? q.result_id : '',
        };
        newdata.push(record);
      }
    });
    const req: RequestInput = {
      body: newdata,
    };
    bloodDataUpdate.execute(req);
    setIsUploaded(true);
  };

  const ref = createRef();

  const RefTextField = forwardRef<unknown, BloodTestQuestion>(
    (question, ref) => (
      <RootineTextField
        control={control}
        id={question.code}
        name={question.code}
        type="number"
        label={question.title}
        value={formData && formData[question.question_id]}
        fullWidth={true}
        placeholder="Enter value"
        // TODO: periodically causing issues for users when value is null,
        // but can't consistently reproduce.
        rules={{
          min: {
            value: Number(question.min_value),
            message: question.min_value_out_of_range_error_message,
          },
          max: {
            value: Number(question.max_value),
            message: question.max_value_out_of_range_error_message,
          },
        }}
      />
    )
  );

  const getContent = (): JSX.Element => {
    return (
      <Grid container rowSpacing={2}>
        {isUploaded ? (
          <Grid item>
            <Grid container flexDirection="column" gap={2}>
              <Alert severity="success"> Results Uploaded! </Alert>

              <RootineButton
                variation="primary"
                size="medium"
                hideIcon
                onClick={() => navigate(`/dashboard`)}
                label="  Go to Dashboard"
              />
            </Grid>
          </Grid>
        ) : (
          <>
            <Grid item>
              <Typography variant="bold">Upload your blood data</Typography>
            </Grid>
            <Grid item xs={12}>
              <form onSubmit={handleSubmit(onSubmit)}>
                {questions &&
                  questions.map((question: BloodTestQuestion) => {
                    return (
                      <Grid
                        item
                        key={question.question_id}
                        sx={{ pt: 1, pb: 1 }}
                      >
                        <RefTextField ref={ref} {...question} />
                      </Grid>
                    );
                  })}
                {questions && (
                  <Grid
                    container
                    item
                    xs={12}
                    justifyContent="flex-end"
                    sx={{ pt: 2 }}
                  >
                    {submitError !== '' && (
                      <Alert severity="error"> {submitError} </Alert>
                    )}
                    {success && (
                      <Alert severity="success"> Results Updated! </Alert>
                    )}
                    <RootineButton
                      label="Upload"
                      id="blood-upload-btn"
                      type="submit"
                      variation="outline_light"
                      hideIcon
                    />
                  </Grid>
                )}
              </form>
            </Grid>
          </>
        )}

        {!questions && <Loading />}
        <Grid item>
          <Typography variant="light" color="text.secondary">
            {disclaimerText}
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="light" color="text.secondary">
            {disclaimerNote}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  const cardProps: CardProps = {
    title: 'BLOOD UPLOAD',
    content: getContent(),
  };
  return (
    <>
      <RootineCard {...cardProps} />
    </>
  );
};
